import { WIDGET_WIDTH } from "@shared/constants";

import type { CustomStyles } from "../types";

import { type WidgetContext } from "./state";

// Initial height for embedded widget (must match skeleton height for smooth transition)
export const EMBED_INITIAL_HEIGHT = 400;

/**
 * Creates a loading skeleton for EMBED mode.
 * Shows an animated pulse effect while the widget loads.
 * Called at the very start of openHallidayPayments for immediate visual feedback.
 */
export const createEmbedSkeleton = (
    targetElement: HTMLElement,
    backgroundColor: string | undefined = undefined,
    customStyles: CustomStyles | undefined = undefined,
    ctx: WidgetContext,
): HTMLDivElement => {
    // Remove any existing skeleton first
    const existingSkeleton = document.getElementById(ctx.ids.skeleton);
    if (existingSkeleton) {
        existingSkeleton.remove();
    }

    // Set up target element for overlay positioning
    if (targetElement.style) {
        targetElement.style.position = "relative";
    }

    // Create wrapper if it doesn't exist
    let wrapper = document.getElementById(ctx.ids.wrapper);
    if (!wrapper) {
        wrapper = document.createElement("div");
        wrapper.id = ctx.ids.wrapper;
        wrapper.style.cssText = `
            position: relative;
            width: ${WIDGET_WIDTH}px;
            max-width: 100%;
            display: block;
        `;
        targetElement.appendChild(wrapper);
    }

    const skeletonBg = backgroundColor || "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)";

    const skeleton = document.createElement("div");
    skeleton.id = ctx.ids.skeleton;
    skeleton.style.cssText = `
        width: 100%;
        height: ${EMBED_INITIAL_HEIGHT}px;
        background: ${skeletonBg};
        background-size: 200% 100%;
        animation: halliday-skeleton-pulse 1.5s ease-in-out infinite;
        border-radius: 12px;
        position: relative;
        z-index: ${customStyles?.zIndex ?? 1};
        transition: opacity 0.3s ease-out;
    `;

    // Add keyframes if not already present
    if (!document.getElementById("halliday-skeleton-styles") && document.head) {
        try {
            const style = document.createElement("style");
            style.id = "halliday-skeleton-styles";
            style.textContent = `
                @keyframes halliday-skeleton-pulse {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
            `;
            document.head.appendChild(style);
        } catch {
            // Ignore in test environments
        }
    }

    wrapper.appendChild(skeleton);
    return skeleton;
};

/**
 * Fades out and removes the skeleton.
 * Called when widget signals it's ready via WIDGET_READY message.
 */
export const removeEmbedSkeleton = (ctx: WidgetContext): void => {
    const skeleton = document.getElementById(ctx.ids.skeleton);
    if (skeleton) {
        skeleton.style.opacity = "0";
        setTimeout(() => skeleton.remove(), 300);
    }
};
